import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** The select's value for "no choice of its own" — a Radix item cannot be empty. */
const INHERIT = 'inherit';

interface RankingStrategySelectProps {
  id: string;
  /** The place's own choice; empty or null for none. */
  value: string | null | undefined;
  /** The regulations there are — keys of the server's `RankingStrategies`. */
  options: string[];
  /**
   * On a stage of a trophy: the trophy's regulation, offered as "inherit from
   * the trophy (…)" and meant by an empty value. Absent on a trophy, or on an
   * event of nobody's circuit, where no choice is the standard ranking.
   */
  inherited?: string | null;
  /** The new choice; empty for none. */
  onChange: (value: string) => void;
}

/**
 * Which regulation a place's classes are ranked by, picked on the Info page:
 * on a trophy for all its stages, on a stage to override it. The rule itself is
 * the server's — this only names it. Changing it changes every ranking of the
 * place at once, those already published included, because places are worked
 * out on every read.
 */
export function RankingStrategySelect({ id, value, options, inherited, onChange }: RankingStrategySelectProps) {
  const { t } = useTranslation();
  const label = (key: string) => t(`gymAdmin.ranking.strategy.options.${key}`, { defaultValue: key });

  const canInherit = inherited != null;
  const selected = value || (canInherit ? INHERIT : 'standard');

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t('gymAdmin.ranking.strategy.label')}</Label>
      <Select value={selected} onValueChange={(next) => onChange(next === INHERIT ? '' : next)}>
        <SelectTrigger id={id} className="sm:w-80" data-testid="ranking-strategy-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {canInherit && (
            <SelectItem value={INHERIT}>
              {t('gymAdmin.ranking.strategy.inherit', { name: label(inherited) })}
            </SelectItem>
          )}
          {options.map((key) => (
            <SelectItem key={key} value={key}>
              {label(key)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{t('gymAdmin.ranking.strategy.help')}</p>
    </div>
  );
}

export default RankingStrategySelect;
